import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetPartitionMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [halls, setHalls] = useState([]);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ parent_hall_id: '', partition_name: '', capacity: 50, is_active: true });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadHalls = async () => {
    const res = await fetch('/api/v1/banquet/halls', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load halls');
    const list = Array.isArray(data) ? data : [];
    setHalls(list);
    if (!form.parent_hall_id && list.length) setForm((prev) => ({ ...prev, parent_hall_id: String(list[0].hall_id) }));
  };

  const loadRows = async () => {
    const q = new URLSearchParams();
    if (form.parent_hall_id) q.set('parent_hall_id', form.parent_hall_id);
    const res = await fetch(`/api/v1/banquet/partitions?${q.toString()}`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load partitions');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadHalls().then(loadRows).catch((err) => setError(err?.message || 'Failed to load data'));
  }, [chainId, propertyId, outletId]);

  useEffect(() => {
    if (chainId && propertyId) loadRows().catch((err) => setError(err?.message || 'Failed to load partitions'));
  }, [form.parent_hall_id]);

  const save = async () => {
    setError('');
    if (!form.parent_hall_id || !form.partition_name || Number(form.capacity || 0) <= 0) {
      return setError('Parent hall, partition name and capacity are required');
    }
    // Validate partition capacity ≤ parent hall capacity
    const selectedHall = halls.find(h => String(h.hall_id) === String(form.parent_hall_id));
    const hallMaxPax = Number(selectedHall?.max_pax || 0);
    if (hallMaxPax > 0 && Number(form.capacity) > hallMaxPax) {
      return setError(`Partition capacity (${form.capacity}) cannot exceed hall "${selectedHall?.hall_name || ''}" capacity (${hallMaxPax})`);
    }
    const url = editingId ? `/api/v1/banquet/partitions/${editingId}` : '/api/v1/banquet/partitions';
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        parent_hall_id: Number(form.parent_hall_id),
        partition_name: form.partition_name,
        capacity: Number(form.capacity),
        is_active: form.is_active !== false
      })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    setEditingId('');
    setForm((prev) => ({ ...prev, partition_name: '', capacity: 50, is_active: true }));
    loadRows();
  };

  const edit = (r) => {
    setEditingId(String(r.partition_id));
    setForm({
      parent_hall_id: String(r.parent_hall_id),
      partition_name: r.partition_name || '',
      capacity: Number(r.capacity || 0),
      is_active: r.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (partitionId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/partitions/${partitionId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadRows();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Partition Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <select value={form.parent_hall_id} onChange={(e) => setForm({ ...form, parent_hall_id: e.target.value })}>
          <option value="">Select parent hall</option>
          {halls.map((h) => <option key={h.hall_id} value={h.hall_id}>{h.hall_name}</option>)}
        </select>
        <input placeholder="Partition name" value={form.partition_name} onChange={(e) => setForm({ ...form, partition_name: e.target.value })} />
        <input type="number" min="1" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Partition' : 'Add Partition'}</button>
        {editingId ? <button onClick={() => { setEditingId(''); setForm((prev) => ({ ...prev, partition_name: '', capacity: 50, is_active: true })); }}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: '8px' }}>
          <thead>
            <tr style={{ background: '#e6f0fa' }}>
              <th style={{ textAlign: 'left' }}>Parent Hall</th>
              <th style={{ textAlign: 'left' }}>Partition</th>
              <th style={{ textAlign: 'right' }}>Capacity</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.partition_id}>
                <td>{r.parent_hall_name}</td>
                <td>{r.partition_name}</td>
                <td style={{ textAlign: 'right' }}>{r.capacity}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.partition_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={5}>No partitions found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetPartitionMaster;
